import React from 'react';
import { Link } from 'react-router-dom';
import SalaryTable from '../../components/blog/SalaryTable';
import CTABox from '../../components/blog/CTABox';
import TipBox from '../../components/blog/TipBox';
import FaqAccordion from '../../components/FaqAccordion';
import AuthorBio from '../../components/blog/AuthorBio';
import { CLOUD_ENGINEER_FAQS } from '../../faqs';

const CloudEngineerGehalt: React.FC = () => {
  const salaryData = [
    { position: 'Cloud Engineer', junior: '48.000 - 60.000 €', mid: '60.000 - 78.000 €', senior: '78.000 - 98.000 €', lead: '95.000 - 118.000 €' },
    { position: 'DevOps Engineer', junior: '50.000 - 62.000 €', mid: '62.000 - 80.000 €', senior: '78.000 - 98.000 €', lead: '95.000 - 120.000 €' },
    { position: 'Platform Engineer', junior: '52.000 - 65.000 €', mid: '65.000 - 84.000 €', senior: '84.000 - 105.000 €', lead: '100.000 - 125.000 €' },
    { position: 'Site Reliability Engineer', junior: '55.000 - 68.000 €', mid: '68.000 - 88.000 €', senior: '88.000 - 110.000 €', lead: '105.000 - 130.000 €' },
    { position: 'Systemadministration Cloud', junior: '42.000 - 52.000 €', mid: '52.000 - 66.000 €', senior: '64.000 - 80.000 €', lead: '78.000 - 95.000 €' },
  ];

  return (
    <article className="blog-article">
      <div className="blog-meta">
        <p><strong>Zuletzt aktualisiert:</strong> 8. September 2026</p>
      </div>

      <p>
        Cloud Engineer, DevOps Engineer, Platform Engineer, SRE: Vier Bezeichnungen,
        die in Stellenanzeigen oft dieselbe Arbeit meinen und trotzdem unterschiedlich
        bezahlt werden. Dieser Beitrag ordnet die Rollen ein, nennt aktuelle Zahlen für
        Deutschland und zeigt, welche Faktoren im Berliner Markt wirklich zählen.
      </p>

      <h2>Was die Zahlen sagen</h2>
      <p>
        Aktuelle Auswertungen für Deutschland ergeben ein erstaunlich uneinheitliches
        Bild:
      </p>
      <ul>
        <li>
          <strong>jobvector</strong> nennt für Cloud Engineers in Berlin ein
          Durchschnittsgehalt von rund 82.458 Euro und weist Berlin damit als
          bestbezahlten Standort aus. Mit akademischem Abschluss liegt der bundesweite
          Schnitt bei etwa 70.422 Euro, mit Berufsausbildung bei 62.500 Euro.
        </li>
        <li>
          <strong>Glassdoor</strong> gibt für DevOps Engineers in Berlin rund 68.500
          Euro an, bei einer typischen Spanne von 57.160 bis 78.480 Euro und einer
          Spitze bei 86.200 Euro. Die Auswertung stützt sich auf etwa 300 Meldungen.
        </li>
        <li>
          <strong>StepStone</strong> nennt für DevOps Engineers einen Durchschnitt von
          55.200 Euro, mit einem Einstiegsgehalt um 50.600 Euro und rund 77.000 Euro
          mit Berufserfahrung.
        </li>
      </ul>
      <p>
        Zwischen 55.200 und 82.458 Euro liegen fast 30.000 Euro, für angeblich
        vergleichbare Rollen. Der Grund ist derselbe wie bei den Architekturrollen: Die
        Bezeichnungen sind nicht geschützt, und jede Quelle zieht einen anderen Schnitt
        durch den Markt. StepStone wertet stark Stellenanzeigen aus dem Mittelstand
        aus, jobvector eher spezialisierte Positionen in Ballungsräumen.
      </p>

      <TipBox title="Die drei Fragen, die den Unterschied machen">
        <p>
          Bauen Sie Infrastruktur oder betreiben Sie sie? Verantworten Sie
          Produktivsysteme mit Rufbereitschaft? Entscheiden Sie über Technologien oder
          setzen Sie Vorgaben um? Je häufiger die Antwort in Richtung Verantwortung
          geht, desto weiter oben liegt das realistische Band.
        </p>
      </TipBox>

      <SalaryTable
        title="Cloud- und Betriebsrollen in Deutschland (2026)"
        rows={salaryData}
        note="Bruttojahresgehalt für Festanstellungen, ohne Bonus und Rufbereitschaftszulagen. Berlin liegt bei diesen Rollen über dem Bundesdurchschnitt, der Abstand zu München und Frankfurt ist durch Remote-Modelle kleiner geworden."
      />

      <h2>Cloud Engineer oder Cloud Architect?</h2>
      <p>
        Die häufigste Verwechslung im Markt. Der Cloud Engineer baut und betreibt: 
        Infrastructure as Code, CI/CD-Pipelines, Monitoring, Störungsbehebung. Der
        Cloud Architect entwirft die Zielarchitektur und entscheidet über Technologien
        und Kosten.
      </p>
      <p>
        Zwischen beiden liegen im oberen Erfahrungsbereich schnell 20.000 bis 30.000
        Euro. Wer Architekturentscheidungen trifft, aber unter der Bezeichnung Engineer
        angestellt ist, verhandelt meist unter Wert. Die Gehaltsspannen für
        Architekturrollen haben wir{' '}
        <Link to="/blog/cloud-architect-gehalt-2026">hier aufgeschlüsselt</Link>.
      </p>

      <h2>Was den Marktwert tatsächlich hebt</h2>

      <h3>Kubernetes in Produktion, nicht im Kurs</h3>
      <p>
        Der einzelne größte Unterschied im Berliner Markt. Ein Cluster im Betrieb
        verantwortet zu haben, inklusive Upgrades und Störungen, zählt deutlich mehr
        als jede Zertifizierung ohne Produktivbezug.
      </p>

      <h3>Mehr als eine Plattform</h3>
      <p>
        Wer AWS und Azure produktiv betrieben hat, ist erheblich seltener als jemand
        mit einer Plattform. Viele Unternehmen betreiben inzwischen hybride
        Landschaften oder planen Migrationen, und dafür fehlen genau diese Profile.
      </p>

      <h3>Rufbereitschaft verhandeln, nicht hinnehmen</h3>
      <p>
        SRE- und Platform-Rollen bringen häufig Bereitschaftsdienste mit. Ob diese
        pauschal vergütet, in Freizeit ausgeglichen oder stillschweigend erwartet
        werden, macht über ein Jahr einen erheblichen Unterschied. Diese Frage gehört
        vor die Vertragsunterschrift.
      </p>

      <h3>Automatisierung schlägt Administration</h3>
      <p>
        Die klassische Systemadministration liegt spürbar unter den Engineering-Rollen.
        Der Übergang gelingt über nachweisbare Automatisierung: Terraform, Ansible,
        Pipelines, Infrastruktur als Code im Repository statt manueller Konfiguration.
      </p>

      <h2>Für Unternehmen</h2>
      <p>
        Cloud- und DevOps-Rollen zählen zu den am schwersten besetzbaren Positionen im
        deutschen IT-Markt. Über alle IT-Positionen hinweg vergehen im Schnitt fast
        acht Monate bis zur Neubesetzung, bei spezialisierten Profilen mehr. Woran das
        liegt und welche vier Stellschrauben messbar wirken, steht in unserem Beitrag
        zum{' '}
        <Link to="/blog/it-fachkraeftemangel-2026-berlin">IT-Fachkräftemangel</Link>.
      </p>
      <p>
        Der häufigste vermeidbare Fehler: eine Anzeige, die Kubernetes, Terraform, drei
        Cloud-Anbieter, Security-Kenntnisse und Entwicklungserfahrung gleichzeitig
        verlangt. Jeder zusätzliche Pflicht-Skill verkleinert den erreichbaren
        Personenkreis multiplikativ.
      </p>

      <h2>Häufige Fragen</h2>
      <FaqAccordion items={CLOUD_ENGINEER_FAQS} />

      <AuthorBio autor="siamak" />

      <p className="sources">
        <strong>Quellen:</strong> jobvector Gehaltsvergleich, Glassdoor
        Gehaltsauswertungen Berlin, StepStone Gehaltsdaten, Bitkom Studienbericht
        Arbeitsmarkt für IT-Fachkräfte, eigene Vermittlungsdaten
      </p>

      <CTABox
        title="Cloud- oder DevOps-Position zu besetzen?"
        description="Schildern Sie uns die Rolle. Sie bekommen eine ehrliche Einschätzung, ob sie zum genannten Gehaltsband im Berliner Markt besetzbar ist."
        buttonText="Position besprechen"
        buttonLink="/kontakt"
      />
    </article>
  );
};

export default CloudEngineerGehalt;
